import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    MenuItem,
    Paper,
    Switch,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import SaveIcon from "@material-ui/icons/Save"
import CloudDoneIcon from "@material-ui/icons/CloudDone"
import { useSnackbar } from "../../../utils/notificacoes"
import {
    obterFlux,
    salvarFlux,
    testarFlux,
    type ErroIntegracaoFluxItem,
    type IntegracaoFluxDados,
    type IntegracaoFluxPayload,
} from "../../../services/integracaoParceiros"
import useLoadingStore from "../../../stores/LoadingStore"
import { useIntegracaoStyles } from "../../../utils/integracaoParceirosStyles"
import { FLUX_LOGO_URL } from "../assets"

interface FormState {
    ambiente: string
    tipoServico: string
    cnpjFlux: string
    ativo: boolean
    cnpjSugerido: string
    credenciaisPreenchidas: boolean
    acionamentoAutomatico: boolean
    tempoPreparoMinutos: string
}

const inicial: FormState = {
    ambiente: "homologacao",
    tipoServico: "RADIUS",
    cnpjFlux: "",
    ativo: false,
    cnpjSugerido: "",
    credenciaisPreenchidas: false,
    acionamentoAutomatico: true,
    tempoPreparoMinutos: "0",
}

// Formata um CNPJ (só dígitos) na máscara 00.000.000/0000-00 para exibição.
const formatarCnpj = (valor: string): string => {
    const d = valor.replace(/\D/g, "").slice(0, 14)
    if (!d) return ""
    return d
        .replace(/^(\d{2})(\d)/, "$1.$2")
        .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/\.(\d{3})(\d)/, ".$1/$2")
        .replace(/(\d{4})(\d)/, "$1-$2")
}

// Verde só quando ligado (mesmo padrão do card da Rede) — a cor vai pela prop `classes`.
const useSwitchVerdeStyles = makeStyles({
    switchBase: {
        "&$checked": { color: "#4caf50" },
        "&$checked + $track": { backgroundColor: "#4caf50" },
    },
    checked: {},
    track: {},
})

export function IntegracaoFluxForm() {
    const classes = useIntegracaoStyles()
    const sw = useSwitchVerdeStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [dados, setDados] = useState<FormState>(inicial)
    const [statusTeste, setStatusTeste] = useState<{ ativa: boolean; mensagem: string } | null>(null)
    const [erros, setErros] = useState<ErroIntegracaoFluxItem[]>([])

    const alertStart = (msg: string, tipo: "success" | "error" | "info") =>
        enqueueSnackbar(msg, { variant: tipo })

    const setCampo = <K extends keyof FormState>(campo: K, valor: FormState[K]) =>
        setDados(prev => ({ ...prev, [campo]: valor }))

    const aplicar = (res: IntegracaoFluxDados) =>
        setDados({
            ambiente: res.ambiente === "producao" ? "producao" : "homologacao",
            tipoServico: res.tipoServico === "TURBO60" ? "TURBO60" : "RADIUS",
            // Se ainda não há CNPJ Flux salvo, sugere o CNPJ do cadastro da loja.
            cnpjFlux: res.cnpjFlux ? res.cnpjFlux : res.cnpjSugerido ?? "",
            ativo: res.ativo,
            cnpjSugerido: res.cnpjSugerido ?? "",
            credenciaisPreenchidas: res.credenciaisPreenchidas,
            acionamentoAutomatico: res.acionamentoAutomatico ?? true,
            tempoPreparoMinutos: String(res.tempoPreparoMinutos ?? 0),
        })

    useEffect(() => {
        const carregar = async () => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            setLoading(true)
            try {
                const res = await obterFlux(autorizacao)
                if ("retornoErro" in res) {
                    alertStart(res.mensagem || "Erro ao carregar integração", "error")
                } else {
                    aplicar(res)
                }
            } finally {
                setLoading(false)
            }
        }
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // O CNPJ é o que identifica a loja na Flux: para ativar, precisa ter 14 dígitos.
    const cnpjValido = () => dados.cnpjFlux.replace(/\D/g, "").length === 14

    const montarPayload = (): IntegracaoFluxPayload => ({
        ambiente: dados.ambiente,
        tipoServico: dados.tipoServico,
        cnpjFlux: dados.cnpjFlux.replace(/\D/g, "") || null,
        ativo: dados.ativo,
        acionamentoAutomatico: dados.acionamentoAutomatico,
        tempoPreparoMinutos: Math.max(0, parseInt(dados.tempoPreparoMinutos, 10) || 0),
    })

    const handleSalvar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        if (dados.ativo && !cnpjValido()) {
            alertStart("Para ativar, informe o CNPJ Flux (14 dígitos).", "error")
            return
        }
        setLoading(true)
        try {
            const res = await salvarFlux(autorizacao, montarPayload())
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao salvar", "error")
            } else {
                aplicar(res)
                alertStart("Configuração salva com sucesso!", "success")
            }
        } finally {
            setLoading(false)
        }
    }

    const handleTestar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await testarFlux(autorizacao)
            // Requisição cancelada/abortada pelo navegador não é falha da Flux — ignora sem alarmar.
            if ("abortado" in res) {
                return
            }
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao testar", "error")
                return
            }
            setStatusTeste({ ativa: res.ativa, mensagem: res.mensagem })
            setErros(res.erros || [])
            alertStart(res.mensagem, res.ativa ? "success" : "error")
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                    <img
                        src={FLUX_LOGO_URL}
                        alt="Flux Delivery"
                        style={{ height: 40, borderRadius: 6 }}
                    />
                    <Typography variant="h5" className={classes.titleText}>
                        Integração Flux Delivery
                    </Typography>
                </Box>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Configuração
                        </Typography>
                        {statusTeste && (
                            <Chip
                                size="small"
                                label={statusTeste.ativa ? "Conexão OK" : "Falha"}
                                style={{
                                    marginLeft: 12,
                                    backgroundColor: statusTeste.ativa ? "#4caf50" : "#e53935",
                                    color: "white",
                                    fontWeight: 600,
                                }}
                            />
                        )}
                    </Box>
                    <Box className={classes.cardContent}>
                        {!dados.credenciaisPreenchidas && (
                            <Typography
                                variant="body2"
                                style={{ color: "#c62828", marginBottom: 12 }}
                            >
                                As credenciais da Flux ainda não foram cadastradas no ambiente. Fale
                                com o suporte antes de ativar.
                            </Typography>
                        )}
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="CNPJ Flux"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formatarCnpj(dados.cnpjFlux)}
                                    onChange={e =>
                                        setCampo("cnpjFlux", e.target.value.replace(/\D/g, ""))
                                    }
                                    helperText="CNPJ cadastrado na Flux — identifica a loja no pedido."
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Ambiente"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.ambiente}
                                    onChange={e => setCampo("ambiente", e.target.value)}
                                >
                                    <MenuItem value="homologacao">Homologação (testes)</MenuItem>
                                    <MenuItem value="producao">Produção</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Tipo de serviço"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.tipoServico}
                                    onChange={e => setCampo("tipoServico", e.target.value)}
                                >
                                    <MenuItem value="RADIUS">RADIUS (padrão)</MenuItem>
                                    <MenuItem value="TURBO60">TURBO60</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Tempo de preparo (min)"
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={dados.tempoPreparoMinutos}
                                    onChange={e =>
                                        setCampo(
                                            "tempoPreparoMinutos",
                                            e.target.value.replace(/\D/g, "")
                                        )
                                    }
                                    disabled={!dados.acionamentoAutomatico}
                                    inputProps={{ min: 0 }}
                                    helperText="Minutos após o pedido antes de acionar o entregador (0 = imediato)."
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={dados.acionamentoAutomatico}
                                            onChange={e =>
                                                setCampo("acionamentoAutomatico", e.target.checked)
                                            }
                                            classes={{
                                                switchBase: sw.switchBase,
                                                checked: sw.checked,
                                                track: sw.track,
                                            }}
                                        />
                                    }
                                    label="Acionar entregador automaticamente"
                                />
                                <Typography
                                    variant="caption"
                                    style={{ color: "#7f8c8d", display: "block" }}
                                >
                                    Desligue para acionamento manual (o operador chama o entregador no
                                    painel do pedido).
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={dados.ativo}
                                            onChange={e => setCampo("ativo", e.target.checked)}
                                            classes={{
                                                switchBase: sw.switchBase,
                                                checked: sw.checked,
                                                track: sw.track,
                                            }}
                                        />
                                    }
                                    label="Ativo"
                                />
                            </Grid>
                        </Grid>

                        {statusTeste && (
                            <Typography
                                variant="body2"
                                style={{
                                    marginTop: 8,
                                    color: statusTeste.ativa ? "#2e7d32" : "#c62828",
                                }}
                            >
                                {statusTeste.mensagem}
                            </Typography>
                        )}

                        <Box display="flex" justifyContent="flex-end" mt={2} style={{ gap: 12 }}>
                            <Button
                                className={classes.secondaryButton}
                                variant="outlined"
                                startIcon={<CloudDoneIcon />}
                                onClick={handleTestar}
                            >
                                Testar conexão
                            </Button>
                            <Button
                                className={classes.saveButton}
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSalvar}
                            >
                                Salvar
                            </Button>
                        </Box>
                        <Typography
                            variant="caption"
                            style={{ color: "#7f8c8d", display: "block", marginTop: 8 }}
                        >
                            O "Testar conexão" verifica a autenticação da Flux — salve antes de testar.
                        </Typography>
                    </Box>
                </Paper>

                {erros.length > 0 && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Últimos erros da integração
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            {erros.map((e, i) => (
                                <Box key={i} mb={1}>
                                    <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                                        {e.data} · {e.operacao}
                                    </Typography>
                                    <Typography variant="body2" style={{ color: "#c62828" }}>
                                        {e.mensagem}
                                    </Typography>
                                    {e.endpoint && (
                                        <Typography
                                            variant="caption"
                                            style={{
                                                color: "#7f8c8d",
                                                display: "block",
                                                wordBreak: "break-all",
                                            }}
                                        >
                                            Endpoint: <code>{e.endpoint}</code>
                                        </Typography>
                                    )}
                                    {e.corpo && (
                                        <details style={{ marginTop: 4 }}>
                                            <summary
                                                style={{
                                                    cursor: "pointer",
                                                    fontSize: 12,
                                                    color: "#7f8c8d",
                                                }}
                                            >
                                                Ver corpo da requisição
                                            </summary>
                                            <pre
                                                style={{
                                                    maxHeight: 220,
                                                    overflow: "auto",
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-all",
                                                    fontSize: 12,
                                                    background: "#f5f5f5",
                                                    padding: 8,
                                                    borderRadius: 4,
                                                    marginTop: 4,
                                                }}
                                            >
                                                {e.corpo}
                                            </pre>
                                        </details>
                                    )}
                                    {i < erros.length - 1 && <Divider style={{ marginTop: 8 }} />}
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                )}
            </Box>
        </Box>
    )
}
